// The fine structure's noise (cloudVolume.ts, setDetail): a seamless billowy 3D texture, the cascade that stacks it into structure at every scale, and the gain table that keeps the mean
// extinction. Pure arithmetic, no DOM and no three, so it can be tested on its own. The shader (erodeX in cloudVolume.ts) and `cascadeRef` here are the same function.
//
// A cloud's edge is self-similar over a wide range of scales (its perimeter is a fractal of dimension about 1.3), so the detail is a cascade: the same billowy noise at octave after octave,
// each half the size of the one above, each one warped by the one above it (so the lobes sit on lobes, as in a turbulent cloud's surface), each weighted by 2^(-H i). H, the Hurst exponent, is
// the cloud's roughness: a small H keeps the small scales strong (a crisp cumulus), a large H lets them die away (a soft stratus). The octaves stop at a physical size (33 m lobes); a pixel only fades out those it could not resolve (a far cloud, a very coarse picture), so the billows are the same size at every screen resolution.

/** The cascade: the first octave's period (m) and its lobe (a period over DET_CELLS cells), the most octaves, and the noise texture's size. Every octave's period (1600 / 2^i), and the two
 *  slow samples' (6400 and 12800 m), divides 12.8 km (NOISE_WRAP in cloudVolume.ts), so the noise frame, which the cloud wind carries, can be kept inside that without a seam. */
export const DET_P0 = 1600;
export const DET_CELLS = 6;
export const DET_L0 = DET_P0 / DET_CELLS;          // 267 m: the biggest lobe is bigger than a cell of the finest ring (125 m), so the model's structure is carried on by lobes of its own size and not only by grain
export const DET_OCT = 4;                          // down to a lobe of 33 m: the finest billow is a size of the cloud, not of the screen's pixels
/** The coarse octaves, for cloud whose own cells are coarse (the world level's 2 km): three lobes above the first, 2133, 1067 and 533 m (periods 12800, 6400 and 3200 m, which divide NOISE_WRAP too).
 *  An octave counts where its lobe is no bigger than about the data's cell (it is weighted out between 1.2 and 2.4 cells), so a ring's finer cells keep the cascade as it was and the world level's
 *  blobs get lobes of their own size, not only grain. Far from the eye, with no ring to give the cloud structure, this is what the cloud's shape is made of: invented, and conserving each cell's mean. */
export const DET_COARSE = 3;
export const DET_LC = DET_L0 * 8;                  // 2133 m
export const DET_PC = DET_P0 * 8;                  // 12800 m
export const COARSE_WARP = 0.2;                    // how far a coarse octave's lobes are bent by the octave above's value (in lobes; the fine ones' is 0.45)
export const COARSE_BEND = 0.3;                    // how far the slow samples move a coarse octave, in lobes
/** How much stronger the erosion is on the world level's cells (2 km), where nothing finer is known and the shape is invented. */
export const WORLD_BOOST = 2.0;
export const DET_N = 64;

export interface DetailNoise { tex: Uint8Array; lut: Uint8Array; mean: number; std: number }

const mod = (a: number, n: number) => ((a % n) + n) % n;
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** Trilinear read of the tiling texture at u (in periods), as the GPU's repeat and linear filter do (texel centres at (i + 0.5) / N). Returns 0..1. */
export function texAt(tex: Uint8Array, ux: number, uy: number, uz: number): number {
  const N = DET_N, cx = ux * N - 0.5, cy = uy * N - 0.5, cz = uz * N - 0.5;
  const x0 = Math.floor(cx), y0 = Math.floor(cy), z0 = Math.floor(cz), fx = cx - x0, fy = cy - y0, fz = cz - z0;
  let s = 0;
  for (let k = 0; k < 2; k++) for (let j = 0; j < 2; j++) for (let i = 0; i < 2; i++) {
    const w = (i ? fx : 1 - fx) * (j ? fy : 1 - fy) * (k ? fz : 1 - fz);
    s += w * tex[(mod(z0 + k, N) * N + mod(y0 + j, N)) * N + mod(x0 + i, N)]!;
  }
  return s / 255;
}

/** The cascade at a grid-frame point g (m): n in 0..1, roughly uniform, with mean a half; `pat` is the slow patchiness (0..1). H: the roughness exponent (0.3 crisp .. 1.2 soft); fp: the footprint of a
 *  pixel at the point (m). The shader's cascadeN, line for line. */
export function cascadeRef(tex: Uint8Array, mean: number, std: number, g: [number, number, number], H: number, fp: number, dxk = 0): { n: number; pat: number } {
  const wa = texAt(tex, g[0] / 6400 + 0.31, g[1] / 6400 + 0.31, g[2] / 6400 + 0.31), wb = texAt(tex, g[0] / 12800 + 0.57, g[1] / 12800 + 0.57, g[2] / 12800 + 0.57);
  const pat = texAt(tex, g[0] / 6400 + 0.12, g[1] / 6400 + 0.12, g[2] / 6400 + 0.12);
  const px = g[0] + (wa - 0.5) * 320, py = g[1] + 0.7 * (wa + wb - 1) * 320, pz = g[2] + (wb - 0.5) * 320;
  let S = 0, V = 0, zp = 0, L = DET_LC, P = DET_PC, w = 1;
  for (let i = 0; i < DET_COARSE + DET_OCT; i++) {
    const coarse = i < DET_COARSE;
    let f = smooth(3.5, 8, L / fp);
    if (coarse) f *= 1 - smooth(1.2 * dxk, 2.4 * dxk, L);
    else if (f <= 0) break;
    if (f > 0) {
      const ii = coarse ? i + 8 : i - DET_COARSE;
      const a = 2.39996 * ii, ox = (0.618 * (ii + 1)) % 1, oy = (0.414 * (ii + 1)) % 1, oz = (0.732 * (ii + 1)) % 1;
      const sh = zp * (coarse ? COARSE_WARP : 0.45) * L;
      const bx = coarse ? (wa - 0.5) * COARSE_BEND * L : 0, by = coarse ? 0.7 * (wa + wb - 1) * COARSE_BEND * L : 0, bz = coarse ? (wb - 0.5) * COARSE_BEND * L : 0;      // (the coarse octaves are bent by the slow samples too, or their cells' straight creases show)
      const t = texAt(tex, (px + bx + Math.cos(a) * sh) / P + ox, (py + by + 0.6 * Math.sin(a * 1.7) * sh) / P + oy, (pz + bz + Math.sin(a) * sh) / P + oz);
      zp = (t - mean) / std;
      const wi = w * f;
      S += wi * zp; V += wi * wi;
    }
    L *= 0.5; P *= 0.5; w *= Math.pow(2, -H);
  }
  const z = S / Math.sqrt(Math.max(V, 1e-6));
  return { n: 1 / (1 + Math.exp(-1.702 * z)), pat };
}

/** A seamless billowy noise (Worley cells, rounded lobes high and the creases between them low) as a 64 cubed texture of bytes with its values spread over 0..255, and the gain table of erodeX in the
 *  shader: for each density (64) and strength (16), the factor that gives the eroded extinction the mean the density had (taken over the cascade's own values, at a middle roughness). Same bytes every time. */
export function makeDetailNoise(): DetailNoise {
  let s = 0x9e3779b9 >>> 0;
  const rnd = () => { s = (s + 0x6d2b79f5) >>> 0; let t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const N = DET_N, N3 = N * N * N, f = new Float32Array(N3), c = DET_CELLS, pts = new Float32Array(c * c * c * 3);
  for (let i = 0; i < pts.length; i++) pts[i] = rnd();
  for (let z = 0; z < N; z++) for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const ux = (x + 0.5) / N * c, uy = (y + 0.5) / N * c, uz = (z + 0.5) / N * c;
    const cx = Math.floor(ux), cy = Math.floor(uy), cz = Math.floor(uz);
    let best = 1e9;
    for (let dz = -1; dz <= 1; dz++) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const ix = cx + dx, iy = cy + dy, iz = cz + dz;
      const k = 3 * ((mod(iz, c) * c + mod(iy, c)) * c + mod(ix, c));
      const ex = ix + pts[k]! - ux, ey = iy + pts[k + 1]! - uy, ez = iz + pts[k + 2]! - uz;
      const d = ex * ex + ey * ey + ez * ez;
      if (d < best) best = d;
    }
    f[(z * N + y) * N + x] = 1 - Math.min(best / 0.81, 1);
  }
  const sorted = Float32Array.from(f).sort(), lo = sorted[Math.floor(0.02 * N3)]!, hi = sorted[Math.floor(0.98 * N3)]!;
  const tex = new Uint8Array(N3);
  let sum = 0, sum2 = 0;
  for (let i = 0; i < N3; i++) {
    tex[i] = Math.round(255 * Math.min(1, Math.max(0, (f[i]! - lo) / (hi - lo))));
    const v = tex[i]! / 255; sum += v; sum2 += v * v;
  }
  const mean = sum / N3, std = Math.sqrt(Math.max(sum2 / N3 - mean * mean, 1e-9));
  // the gain table: the mean over the cascade's values (sampled at random points, at a middle roughness, every octave on) of the eroded extinction, against the extinction the density has
  const S = 4096, nn = new Float32Array(S);
  for (let i = 0; i < S; i++) nn[i] = cascadeRef(tex, mean, std, [rnd() * 25600, rnd() * 6000, rnd() * 25600], 0.7, 0.01).n;
  const lut = new Uint8Array(64 * 16);
  for (let ki = 0; ki < 16; ki++) for (let di = 0; di < 64; di++) {
    const k = ki / 15, d = di / 63;
    let m = 0;
    for (let i = 0; i < S; i++) { const e = k * (1 - nn[i]!), r = Math.min(1, Math.max(0, (d - e) / (1 - e))); m += r * r; }
    m /= S;
    const gain = m > 1e-9 ? Math.min(4, Math.max(1, d * d / m)) : 4;
    lut[ki * 64 + di] = Math.round(255 * (gain - 1) / 3);
  }
  return { tex, lut, mean, std };
}
